/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { listPendingApprovals, approveTrade, rejectTrade } from '../api/api';
import ConfirmModal from '../components/ui/ConfirmModal';
import { toast } from 'sonner';
import { CheckCircle, XCircle, Clock, ArrowUpCircle, ArrowDownCircle, Bot, BrainCircuit, Loader2, Timer } from 'lucide-react';
import { TRADE_APPROVAL_CHANGED, emitTradeApprovalChange } from '../lib/tradeApprovalEvents';

const statusBadge = (status) => {
  if (status === 'APPROVED') return <span className="px-2 py-0.5 text-xs rounded-full bg-green-500/20 text-green-400">Approved</span>;
  if (status === 'REJECTED') return <span className="px-2 py-0.5 text-xs rounded-full bg-red-500/20 text-red-400">Rejected</span>;
  if (status === 'EXPIRED') return <span className="px-2 py-0.5 text-xs rounded-full bg-gray-500/20 text-gray-400">Expired</span>;
  return <span className="px-2 py-0.5 text-xs rounded-full bg-amber-500/20 text-amber-400">Pending</span>;
};

const directionBadge = (dir) => {
  if (dir === 'BUY') return <span className="inline-flex items-center gap-1 text-green-400"><ArrowUpCircle className="h-3.5 w-3.5" /> BUY</span>;
  if (dir === 'SELL') return <span className="inline-flex items-center gap-1 text-red-400"><ArrowDownCircle className="h-3.5 w-3.5" /> SELL</span>;
  return <span>{dir}</span>;
};

const sourceIcon = (source) => source === 'AI'
  ? <BrainCircuit className="h-4 w-4 text-purple-400" />
  : <Bot className="h-4 w-4 text-blue-400" />;

function timeAgo(dateStr) {
  const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export default function PendingTradesPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('PENDING');
  const [actionId, setActionId] = useState(null);
  const [actionType, setActionType] = useState(null); // 'approve' or 'reject'
  const [actionBusy, setActionBusy] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const actionItem = items.find(i => i.id === actionId);
  const isDurableAction = actionItem?.approval_kind === 'durable_runtime';

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const data = await listPendingApprovals(statusFilter);
      setItems(Array.isArray(data) ? data : data?.results || []);
    } catch {
      toast.error('Failed to load pending trades');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Re-fetch when a trade-approval event fires (WS notification or action in another tab/page)
  useEffect(() => {
    const handler = () => fetchData();
    window.addEventListener(TRADE_APPROVAL_CHANGED, handler);
    return () => window.removeEventListener(TRADE_APPROVAL_CHANGED, handler);
  }, [fetchData]);

  const handleAction = async () => {
    if (!actionId || !actionType) return;
    setActionBusy(true);
    try {
      if (actionType === 'approve') {
        await approveTrade(actionId);
        toast.success(isDurableAction ? 'Approval submitted' : 'Trade approved & executed');
      } else {
        await rejectTrade(actionId, rejectReason);
        toast.success(isDurableAction ? 'Approval rejected' : 'Trade rejected');
      }
      setItems(prev => prev.filter(i => i.id !== actionId));
      emitTradeApprovalChange();
    } catch (e) {
      toast.error(e.message || `Failed to ${actionType} trade`);
    } finally {
      setActionBusy(false);
      setActionId(null);
      setActionType(null);
      setRejectReason('');
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl sm:text-2xl font-bold text-textPrimary">Trade Approvals</h1>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="bg-backgroundSurface border border-border rounded px-3 py-1.5 text-sm text-textPrimary"
        >
          <option value="PENDING">Pending</option>
          <option value="APPROVED">Approved</option>
          <option value="REJECTED">Rejected</option>
          <option value="EXPIRED">Expired</option>
          <option value="ALL">All</option>
        </select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 text-textSecondary">
          No {statusFilter === 'ALL' ? '' : statusFilter.toLowerCase() + ' '}trade approvals.
        </div>
      ) : (
        <div className="grid gap-3">
          {items.map(item => {
            const isDurableRuntime = item.approval_kind === 'durable_runtime';
            return (
            <div
              key={item.id}
              onClick={() => navigate(`/approvals/${item.id}`)}
              className="bg-backgroundSurface border border-border rounded-lg p-4 cursor-pointer
                         hover:border-primary/40 transition group"
            >
              <div className="flex items-start justify-between gap-3">
                {/* Left */}
                <div className="flex items-center gap-3 min-w-0">
                  {sourceIcon(item.source)}
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-textPrimary">{item.symbol}</span>
                      {directionBadge(item.direction)}
                      {statusBadge(item.status)}
                    </div>
                    <div className="text-xs text-textSecondary mt-0.5 truncate">
                      {isDurableRuntime
                        ? (item.reason || item.source_display || 'Runtime approval')
                        : <>{item.validated_lot} lot &middot; SL {item.validated_sl} &middot; TP {item.validated_tp}</>}
                      {item.source_display && <span> &middot; {item.source_display}</span>}
                    </div>
                  </div>
                </div>

                {/* Right */}
                <div className="flex items-center gap-2 shrink-0">
                  {item.status === 'PENDING' && item.expires_at && (() => {
                    const ms = new Date(item.expires_at).getTime() - Date.now();
                    if (ms <= 0) return <span className="text-xs text-red-400 flex items-center gap-0.5"><Timer className="h-3 w-3" /> Expired</span>;
                    const m = Math.floor(ms / 60000);
                    return <span className={`text-xs flex items-center gap-0.5 ${m <= 5 ? 'text-amber-400' : 'text-textSecondary'}`}><Timer className="h-3 w-3" /> {m}m left</span>;
                  })()}
                  <span className="text-xs text-textSecondary">{timeAgo(item.created_at)}</span>
                  {item.status === 'PENDING' && (
                    <div className="flex gap-1.5" onClick={e => e.stopPropagation()}>
                      <button
                        className="p-1.5 rounded-full bg-green-500/10 text-green-400 hover:bg-green-500/20 transition"
                        title="Approve"
                        onClick={() => { setActionId(item.id); setActionType('approve'); }}
                      >
                        <CheckCircle className="h-4 w-4" />
                      </button>
                      <button
                        className="p-1.5 rounded-full bg-red-500/10 text-red-400 hover:bg-red-500/20 transition"
                        title="Reject"
                        onClick={() => { setActionId(item.id); setActionType('reject'); }}
                      >
                        <XCircle className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
            );
          })}
        </div>
      )}

      <ConfirmModal
        open={!!actionId}
        title={
          actionType === 'approve'
            ? (isDurableAction ? 'Approve Request' : 'Approve Trade')
            : (isDurableAction ? 'Reject Request' : 'Reject Trade')
        }
        message={
          actionType === 'approve'
            ? 'Approve this request now?'
            : <div className="space-y-3">
                <p>
                  {isDurableAction
                    ? 'Reject this request? The AI system run will stop.'
                    : 'Reject this request? It will not be executed.'}
                </p>
                <textarea
                  className="w-full bg-background border border-border rounded px-3 py-2 text-sm text-textPrimary resize-none"
                  rows={3}
                  placeholder="Reason for rejection (optional)"
                  value={rejectReason}
                  onChange={e => setRejectReason(e.target.value)}
                  onClick={e => e.stopPropagation()}
                />
              </div>
        }
        confirmLabel={actionType === 'approve' ? 'Approve' : 'Reject'}
        onConfirm={handleAction}
        onCancel={() => { setActionId(null); setActionType(null); setRejectReason(''); }}
        busy={actionBusy}
      />
    </div>
  );
}
